import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SchedulingLinkStandardAvailabilityBlockModel} from '../../../../models/api/schedulingLinkModel';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {StandardAvailabilityCalendarContainer} from '../../../calendars/scheduling/availability/standardAvailabilityCalendarContainer';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {Frame} from '../../../common/frames/frame';
import {MeetingTimesData} from './meetingsettings/meetingSettingsPanel';
import {MeetingTimesDialogRightPanel} from './meetingTimesDialogRightPanel';

/*
 * Types.
 */

export interface MeetingTimesDialogPublicProps {
  panelId: CalendarsPanelId;
  onDialogClose: Handler;
  onInsertMeetingTimes: HandlerOf<MeetingTimesData>;
  schedulingLinkId?: number;
}

interface MeetingTimesDialogProps extends MeetingTimesDialogPublicProps {
  didEditDraft: boolean;
  shouldForcePopout: boolean;
  selectedSlots: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>;
  onUpdateSelectedSlots: HandlerOf<ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>>;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  height: 100%;
  width: 100%;
`;

/*
 * Component.
 */

export const MeetingTimesDialog: FC<MeetingTimesDialogProps> = (props) => {
  const [isConfirmationOpen, toggleIsConfirmationOpen] = useToggle();

  const {shouldForcePopout, selectedSlots, onUpdateSelectedSlots} = props;
  const [shouldFramePopout, setShouldFramePopout] = useState(shouldForcePopout);

  return (
    <Frame
      onRequestClose={(forceClose) => {
        // Close the dialog without confirmation if we didn't edit or this is a force close.
        if (forceClose || !props.didEditDraft) {
          props.onDialogClose();
        } else {
          toggleIsConfirmationOpen();
        }
      }}
      minWidth="80vw"
      minHeight="80vh"
      width={1000}
      height={600}
      shouldForcePopout={shouldFramePopout}
      onOpenPopout={() => setShouldFramePopout(true)}
      shouldHideDialogHeader
    >
      <StyledWrapperDiv>
        <StandardAvailabilityCalendarContainer
          panelId={props.panelId}
          renderRightPanel={() => renderRightPanel(props, toggleIsConfirmationOpen)}
          selectedSlots={selectedSlots}
          setSelectedSlots={onUpdateSelectedSlots}
        />
      </StyledWrapperDiv>
      {isConfirmationOpen && renderCloseConfirmation(props, toggleIsConfirmationOpen)}
    </Frame>
  );
};

/*
 * Helpers.
 */

function renderCloseConfirmation(props: MeetingTimesDialogProps, onConfirmationClose: Handler) {
  return (
    <OkCancelDialog
      title={<Trans id="composer-insertmeetingtimes-dialogclosetitle">Close scheduling times</Trans>}
      okButtonLabel={<Trans id="composer-insertmeetingtimes-dialogclosebutton">Close</Trans>}
      onOkClick={props.onDialogClose}
      onRequestClose={onConfirmationClose}
    >
      <Trans id="composer-insertmeetingtimes-dialogclosemessage">
        By closing this window, all your changes will be lost. Are you sure you want to continue?
      </Trans>
    </OkCancelDialog>
  );
}

function renderRightPanel(props: MeetingTimesDialogProps, onConfirmationOpen: Handler) {
  return (
    <MeetingTimesDialogRightPanel
      onRequestClose={() => {
        // If the user made any changes, show a confirmation before closing.
        if (props.didEditDraft) {
          onConfirmationOpen();
        } else {
          props.onDialogClose();
        }
      }}
      onSelectMeetingTimes={(meetingTimesData) => props.onInsertMeetingTimes(meetingTimesData)}
      panelId={props.panelId}
    />
  );
}
